import React from 'react';
import MetricSummaryTrendCardLoading from 'src/components/BeaconV2Components/MetricSummaryTrendCard/MetricSummaryTrendCardLoading';
import { MetricSummaryTrendCardProps } from 'src/components/BeaconV2Components/MetricSummaryTrendCard/MetricSummaryTrendCard';
import MetricSummaryTrendCardV2, {
  MetricSummaryTrendCardV2Props
} from 'src/components/BeaconV2Components/MetricSummaryTrendCard/MetricSummaryTrendCardV2';
import { UseMetricByWeekIdArgs } from 'src/serverProxy/useMetricByWeekId';
import { useMetricByWeekIdV2, useDerivedMetricByWeekIdV2 } from 'src/components/BeaconV2Components/SplineChartV2';
import { INDEX_FIELDS } from 'src/utils/entityDefinitions';
import { getAppName, shouldShowNewAtlas } from 'src/utils/app';
import { getDirection } from 'src/components/BeaconV2Components/utils/chartStyles';
import _get from 'lodash/get';

export const getCardTitle = (
  indexName: string,
  fieldName: string,
  titleOverrides?: Record<string, Record<string, string>>
) => {
  return (
    _get(titleOverrides, [indexName, fieldName]) ||
    INDEX_FIELDS.getField(getAppName(), indexName, fieldName).displayName
  );
};

export const getCardMetricType = (indexName: string, fieldName: string) => {
  return INDEX_FIELDS.getField(getAppName(), indexName, fieldName).metricType;
};

export interface SmartKeyMetricCardProps extends UseMetricByWeekIdArgs {
  variant: 'large' | 'small' | 'medium';
  title?: string;
  onClick?: () => void;
  shouldSliceByWeekRange?: boolean;
  buildMetricSummaryTrendCardProps?: (defaultProps: MetricSummaryTrendCardProps) => MetricSummaryTrendCardProps;
}

/**
 * Atlas V2 Key Metric Card - uses useMetricByWeekIdV2 hook which returns V2 format directly
 */
const AtlasKeyMetricCard = ({
  indexName,
  fieldName,
  variant,
  title,
  onClick,
  buildMetricSummaryTrendCardProps,
  entityType,
  primaryAggregationBuilder,
  secondaryAggregationBuilder,
  requestBuilder,
  ...rest
}: SmartKeyMetricCardProps) => {
  const {
    isLoading,
    field,
    primarySeries,
    comparisonSeries,
    formattedPrimaryTotal,
    formattedPercentChange,
    percentChange,
    formattedLastPrimaryValue,
    formattedLastValuePercentChange,
    lastValuePercentChange
  } = useMetricByWeekIdV2({
    fieldName,
    indexName,
    entityType,
    primaryAggregationBuilder,
    secondaryAggregationBuilder,
    requestBuilder
  });

  // Even though content score and review rating has lastValue timeAgg function, we still want to show the average
  const ATLAS_AVERAGE_EXCEPTION = ['reviewRating'];

  const useLatestWeek =
    field && field.timePeriodAggregationFunctionType === 'lastValue' && !ATLAS_AVERAGE_EXCEPTION.includes(field.name);

  const mainMetric = useLatestWeek ? formattedLastPrimaryValue : formattedPrimaryTotal;
  const rawChange = useLatestWeek ? lastValuePercentChange : percentChange;
  const change = useLatestWeek ? formattedLastValuePercentChange : formattedPercentChange;

  const metricType = getCardMetricType(indexName, fieldName);

  return isLoading ? (
    <MetricSummaryTrendCardLoading variant={variant} />
  ) : (
    <MetricSummaryTrendCardV2
      variant={variant}
      title={title || getCardTitle(indexName, fieldName)}
      primarySeries={primarySeries}
      comparisonSeries={comparisonSeries}
      metricType={metricType}
      value={mainMetric}
      change={change}
      direction={getDirection(rawChange)}
      selected={false}
      onClick={onClick}
    />
  );
};

/**
 * Atlas V2 Derived Key Metric Card - uses useDerivedMetricByWeekIdV2 hook which properly
 * handles derived fields like purchaseRate (conversion) that combine multiple data sources
 */
const AtlasV2DerivedKeyMetricCard = ({
  indexName,
  fieldName,
  variant,
  title,
  onClick,
  buildMetricSummaryTrendCardProps
}: SmartKeyMetricCardProps) => {
  const {
    isLoading,
    primarySeries,
    comparisonSeries,
    formattedPrimaryTotal,
    formattedPercentChange,
    percentChange,
    metricType
  } = useDerivedMetricByWeekIdV2({
    fieldName,
    indexName
  });

  return isLoading ? (
    <MetricSummaryTrendCardLoading variant={variant} />
  ) : (
    <MetricSummaryTrendCardV2
      variant={variant}
      title={title || getCardTitle(indexName, fieldName)}
      primarySeries={primarySeries}
      comparisonSeries={comparisonSeries}
      metricType={metricType}
      value={formattedPrimaryTotal}
      change={formattedPercentChange}
      direction={getDirection(percentChange)}
      selected={false}
      onClick={onClick}
    />
  );
};

/**
 * Key metric card for a derived field - uses useDerivedMetricByWeekIdV2 hook which properly
 * handles derived fields like purchaseRate (conversion) that combine multiple data sources
 */
const DerivedKeyMetricCard = ({
  indexName,
  fieldName,
  variant,
  title,
  onClick,
  buildMetricSummaryTrendCardProps
}: SmartKeyMetricCardProps) => {
  const {
    isLoading,
    primarySeries,
    comparisonSeries,
    formattedPrimaryTotal,
    formattedPercentChange,
    percentChange,
    metricType
  } = useDerivedMetricByWeekIdV2({
    fieldName,
    indexName
  });

  return isLoading ? (
    <MetricSummaryTrendCardLoading variant={variant} />
  ) : (
    <MetricSummaryTrendCardV2
      variant={variant}
      title={title || getCardTitle(indexName, fieldName)}
      primarySeries={primarySeries}
      comparisonSeries={comparisonSeries}
      metricType={metricType}
      value={formattedPrimaryTotal}
      change={formattedPercentChange}
      direction={getDirection(percentChange)}
      selected={false}
      onClick={onClick}
    />
  );
};

/**
 * Key metric card for all fields except derived - uses V2 hooks
 */
const KeyMetricCard = ({
  indexName,
  fieldName,
  variant,
  title,
  onClick,
  buildMetricSummaryTrendCardProps,
  entityType,
  primaryAggregationBuilder,
  secondaryAggregationBuilder,
  requestBuilder,
  ...rest
}: SmartKeyMetricCardProps) => {
  const {
    isLoading,
    field,
    primarySeries,
    comparisonSeries,
    formattedPrimaryTotal,
    formattedPercentChange,
    percentChange,
    formattedLastPrimaryValue,
    formattedLastValuePercentChange,
    lastValuePercentChange
  } = useMetricByWeekIdV2({
    fieldName,
    indexName,
    entityType,
    primaryAggregationBuilder,
    secondaryAggregationBuilder,
    requestBuilder
  });

  const useLatestWeek = field && field.timePeriodAggregationFunctionType === 'lastValue';
  const mainMetric = useLatestWeek ? formattedLastPrimaryValue : formattedPrimaryTotal;
  const rawChange = useLatestWeek ? lastValuePercentChange : percentChange;
  const change = useLatestWeek ? formattedLastValuePercentChange : formattedPercentChange;

  const metricType = getCardMetricType(indexName, fieldName);

  const getMetricSummaryTrendCardV2Props = (): Omit<MetricSummaryTrendCardV2Props, 'variant'> => ({
    title: title || getCardTitle(indexName, fieldName),
    primarySeries,
    comparisonSeries,
    metricType,
    value: mainMetric,
    change,
    direction: getDirection(rawChange),
    selected: false,
    onClick
  });

  return isLoading ? (
    <MetricSummaryTrendCardLoading variant={variant} />
  ) : (
    <MetricSummaryTrendCardV2 variant={variant} {...getMetricSummaryTrendCardV2Props()} />
  );
};

/**
 * Given an index name and field name, it fetches the data
 * and displays it inside of a key metric summary card.
 * We call it a "smart" key metric card because it handles all
 * the logic of fetching, loading states, and display.
 */
const SmartKeyMetricCard = (props: SmartKeyMetricCardProps) => {
  const field = INDEX_FIELDS.getField(getAppName(), props.indexName, props.fieldName);

  if (shouldShowNewAtlas()) {
    return field.aggregationFunctionType === 'derived' ? (
      <AtlasV2DerivedKeyMetricCard {...props} />
    ) : (
      <AtlasKeyMetricCard {...props} />
    );
  }

  return field.aggregationFunctionType === 'derived' ? (
    <DerivedKeyMetricCard {...props} />
  ) : (
    <KeyMetricCard {...props} />
  );
};

export default SmartKeyMetricCard;
